import React, { useContext, useEffect, useState, useCallback } from 'react';
import { Row, notification } from 'antd';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

import { useMenu, useFetch } from '../../hooks';
import { useAddons } from '../../hooks/useMenuQuery';
import { LayoutContext } from '../../contexts';
import { URLS } from '../../constants';
import CreateUpdateProductForm from '../../components/CreateUpdateProductForm';
import axios from 'axios';
import store from 'store';
import { baseURL, getMenuId } from '../../utils/helpers';

const authToken = store.get('authenticationToken');

function ProductCreate({ history }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const { setHeaderComponent } = useContext(LayoutContext);
  const { doFetch: createProductFetch } = useFetch();
  const { doFetch: uploadProductImageFetch } = useFetch();
  const { doFetch: fetchLanguages } = useFetch();
  const { doFetch: fetchCategoryDetails } = useFetch();
  const menuData = useMenu();

  // Pre-selected category state
  const [preselectedCategoryId, setPreselectedCategoryId] = useState(null);
  const [preselectedCategory, setPreselectedCategory] = useState(null);
  const [productForm, setProductForm] = useState(null); // Reference to the form instance

  // Search states
  const [searchCategories, setSearchCategories] = useState([]);
  const [searchSubcategories, setSearchSubcategories] = useState([]);
  const [searchBrands, setSearchBrands] = useState([]);
  const [isSearchingCategories, setIsSearchingCategories] = useState(false);
  const [isSearchingSubcategories, setIsSearchingSubcategories] = useState(false);
  const [isSearchingBrands, setIsSearchingBrands] = useState(false);
  const [languages, setLanguages] = useState([]);

  const menuId = getMenuId();
  const { data: addonsData } = useAddons(menuId, 1, 500);

  // Search handlers
  const handleSearchCategories = useCallback(async (searchQuery) => {
    if (!searchQuery || searchQuery.length < 1) {
      setSearchCategories([]);
      return;
    }

    setIsSearchingCategories(true);
    try {
      const response = await axios.get(
        `${baseURL}${URLS.searchCategories()}`,
        {
          params: { q: searchQuery, per_page: 20 },
          headers: { Authorization: authToken },
        }
      );

      // Transform search results to match the structure of original categoryList
      let transformedCategories = (response.data.categories || []).map(cat => ({
        id: cat.id,
        translations: cat.category_translations.map(translation => ({
          lanId: translation.language_id,
          lanName: languages.find(lang => lang.id === translation.language_id)?.name || `Language ${translation.language_id}`,
          name: translation.name_text,
        })),
        image: cat.image_url,
        tags: [], // Keep empty for compatibility
        productsCount: 0, // Not available in search results
      }));

      // Ensure preselected category is included in search results if it exists
      if (preselectedCategory) {
        const preselectedExists = transformedCategories.find(cat => cat.id === preselectedCategory.id) ||
                                  menuData.categoryList?.find(cat => cat.id === preselectedCategory.id);
        if (!preselectedExists && !transformedCategories.find(cat => cat.id === preselectedCategory.id)) {
          transformedCategories = [preselectedCategory, ...transformedCategories];
        }
      }

      setSearchCategories(transformedCategories);
    } catch (error) {
      console.error('Error searching categories:', error);
      setSearchCategories([]);
    } finally {
      setIsSearchingCategories(false);
    }
  }, [languages, preselectedCategory, menuData.categoryList]);

  const handleSearchSubcategories = useCallback(async (searchQuery) => {
    if (!searchQuery || searchQuery.length < 1) {
      setSearchSubcategories([]);
      return;
    }

    setIsSearchingSubcategories(true);
    try {
      const response = await axios.get(
        `${baseURL}${URLS.searchSubcategories()}`,
        {
          params: { q: searchQuery, per_page: 20 },
          headers: { Authorization: authToken },
        }
      );

      // Transform search results to match the structure of original subcategoryList
      const transformedSubcategories = (response.data.subcategories || []).map(sub => ({
        id: sub.id,
        categoryId: sub.category_id,
        translations: sub.subcategory_translations.map(translation => ({
          lanId: translation.language_id,
          lanName: languages.find(lang => lang.id === translation.language_id)?.name || `Language ${translation.language_id}`,
          name: translation.name_text,
        })),
        image: sub.image_url,
        productsCount: 0, // Not available in search results
      }));

      setSearchSubcategories(transformedSubcategories);
    } catch (error) {
      console.error('Error searching subcategories:', error);
      setSearchSubcategories([]);
    } finally {
      setIsSearchingSubcategories(false);
    }
  }, [languages]);

  const handleSearchBrands = useCallback(async (searchQuery) => {
    if (!searchQuery || searchQuery.length < 1) {
      setSearchBrands([]);
      return;
    }

    setIsSearchingBrands(true);
    try {
      const response = await axios.get(
        `${baseURL}${URLS.searchBrands()}`,
        {
          params: { q: searchQuery, per_page: 20 },
          headers: { Authorization: authToken },
        }
      );

      // Transform search results to match the structure of original brandList
      const transformedBrands = (response.data.brands || []).map(brand => ({
        id: brand.id,
        translations: brand.brand_translations.map(translation => ({
          lanId: translation.language_id,
          lanName: languages.find(lang => lang.id === translation.language_id)?.name || `Language ${translation.language_id}`,
          name: translation.name_text,
        })),
        image: brand.image_url,
        productsCount: 0, // Not available in search results
      }));

      setSearchBrands(transformedBrands);
    } catch (error) {
      console.error('Error searching brands:', error);
      setSearchBrands([]);
    } finally {
      setIsSearchingBrands(false);
    }
  }, [languages]);

  useEffect(() => {
    setHeaderComponent('Create New Product');

    return () => {
      setHeaderComponent(null);
    };
  }, []);

  // Load languages
  useEffect(() => {
    fetchLanguages({
      url: URLS.languages,
      onSuccess: (data) => {
        setLanguages(data);
      },
      onError: () => {
        console.warn('Failed to load languages, using empty list');
        setLanguages([]);
      },
    });
  }, [fetchLanguages]);

  // Handle categoryId query parameter
  useEffect(() => {
    const categoryIdParam = searchParams.get('categoryId');
    if (categoryIdParam) {
      const categoryId = parseInt(categoryIdParam, 10);
      setPreselectedCategoryId(categoryId);

      // Check if category already exists in menuData
      const existingCategory = menuData.categoryList?.find(cat => cat.id === categoryId);
      if (existingCategory) {
        setPreselectedCategory(existingCategory);
        // Update form field if form is ready
        if (productForm) {
          console.log('productForm', productForm.getFieldsValue());
          console.log('categoryId', categoryId);
          productForm.setFieldsValue({ ...productForm.getFieldsValue(), categoryId: categoryId });
        }
      } else {
        // Fetch category details if not in the loaded list
        fetchCategoryDetails({
          url: URLS.categoryDetails(categoryId),
          onSuccess: (responseData) => {
            // API returns {category: {...}}, so access the category object
            const categoryData = responseData.category;
            if (!categoryData) {
              console.warn('Invalid category data structure received');
              setPreselectedCategoryId(null);
              return;
            }

            // Transform category data to match expected format
            const translations = Array.isArray(categoryData.category_translations)
              ? categoryData.category_translations.map(translation => ({
                  lanId: translation.language_id,
                  lanName: languages.find(lang => lang.id === translation.language_id)?.name ||
                           menuData.languageList?.find(lang => lang.id === translation.language_id)?.name ||
                           `Language ${translation.language_id}`,
                  name: translation.name_text,
                }))
              : []; // Fallback to empty array if translations is not available

            const transformedCategory = {
              id: categoryData.id,
              translations,
              image: categoryData.image_url,
              tags: [],
              productsCount: 0,
            };
            setPreselectedCategory(transformedCategory);
            // Update form field if form is ready
            if (productForm) {
              productForm.setFieldsValue({ ...productForm.getFieldsValue(), categoryId: categoryId });
            }
          },
          onError: (error) => {
            console.warn(`Failed to load category details for ID: ${categoryId}`, error);
            setPreselectedCategoryId(null);
          },
        });
      }
    } else {
      // Clear preselection if no categoryId param
      setPreselectedCategoryId(null);
      setPreselectedCategory(null);
    }
  }, [searchParams, menuData.categoryList, fetchCategoryDetails]);

  const updateColorsWithUploadedImages = async (productData, formValues, uploadInfo) => {
    // Check if any colors have image links that need updating
    const hasColorsWithImages = formValues.colors?.some(color => color.imageLink);

    if (!hasColorsWithImages) {
      return;
    }

    if (!uploadInfo?.uploadedFiles?.length || !uploadInfo?.uploadedImages?.length) {
      return;
    }

    // Create mapping between uploaded files and their backend URLs and IDs
    const fileToImageDataMap = new Map();
    uploadInfo.uploadedFiles.forEach((file, index) => {
      const uploadedImage = uploadInfo.uploadedImages[index];

      if (uploadedImage && file.originFileObj) {
        const backendUrl = uploadedImage.image_url || uploadedImage.url;
        const backendId = uploadedImage.id; // Get the backend image ID
        const imageData = { url: backendUrl, id: backendId };

        // Map by file reference object
        fileToImageDataMap.set(file.originFileObj, imageData);
        // Also map by file identifier as fallback
        const fileKey = `${file.originFileObj.name}-${file.originFileObj.size}-${file.originFileObj.lastModified}`;
        fileToImageDataMap.set(fileKey, imageData);
      }
    });

    // Update colors with actual backend URLs, IDs, and format hexcode
    const updatedColors = formValues.colors.map((color) => {
      // Convert hexcode object to string (same as in createNewProduct)
      const formattedColor = {
        ...color,
        hexcode: typeof color.hexcode === 'object' ? color.hexcode.toHexString() : color.hexcode
      };

      if (!color.imageLink || !color.linkedImageFile) {
        return formattedColor;
      }

      // Try to find the matching uploaded image data by object reference first
      let imageData = fileToImageDataMap.get(color.linkedImageFile);

      // If not found by reference, try by file identifier
      if (!imageData) {
        const fileKey = `${color.linkedImageFile.name}-${color.linkedImageFile.size}-${color.linkedImageFile.lastModified}`;
        imageData = fileToImageDataMap.get(fileKey);
      }

      if (imageData) {
        // Update with the actual backend URL and ID
        formattedColor.imageLink = imageData.url;
        if (imageData.id) {
          formattedColor.linkedImageId = imageData.id; // Set the actual backend image ID
        } else {
          delete formattedColor.linkedImageId; // Remove if no ID available
        }
        delete formattedColor.linkedImageFile; // Remove the file reference
        return formattedColor;
      }

      // If no match found but imageLink is a blob URL, remove it to avoid broken images
      if (color.imageLink && color.imageLink.startsWith('blob:')) {
        delete formattedColor.imageLink;
        delete formattedColor.linkedImageFile;
        delete formattedColor.linkedImageId;
        return formattedColor;
      }

      return formattedColor;
    });

    // Update the product with the corrected color image links
    try {
      await axios.patch(
        `${baseURL}${URLS.updateProduct({ productId: productData.id })}`,
        {
          colors: JSON.stringify(updatedColors)
        },
        {
          headers: {
            Authorization: `${authToken}`,
            'Content-Type': 'application/json',
          },
        }
      );
    } catch (error) {
      console.error('Error updating colors with image URLs:', error);
      console.error('Error response:', error.response?.data);
    }
  };

  const uploadProductImage = async (productData, formValues) => {
    const hasMainImage = formValues?.image?.file?.originFileObj;
    const hasAdditionalImages = formValues.uploadFileList.length || formValues.deleteFileList.length;

    if (!hasMainImage && !hasAdditionalImages) {
      notification.success({
        placement: 'bottomRight',
        message: 'Product created successfully.',
        duration: 3,
      });
      // Invalidate products cache to refetch lists with the new product
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['search-products'] });
      navigate('/');
      return;
    } else if (
      !hasMainImage &&
      hasAdditionalImages
    ) {
      const uploadInfo = await updateOtherProductImages(productData, formValues);
      await updateColorsWithUploadedImages(productData, formValues, uploadInfo);
      notification.success({
        placement: 'bottomRight',
        message: 'Product created successfully.',
        duration: 3,
      });
      // Invalidate products cache to refetch lists with the new product
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['search-products'] });
      navigate('/');
      return;
    } else if (
      hasMainImage &&
      !hasAdditionalImages
    ) {
      await uploadProductImageFetch({
        method: 'FILE_POST',
        url: URLS.uploadProductImage({
          productId: productData?.id,
        }),
        params: { image: formValues.image.file.originFileObj },
        showSuccessNotification: true,
        successMessage: 'Product created successfully.',
        onSuccess: () => {
          // Invalidate products cache to refetch lists with the new product
          queryClient.invalidateQueries({ queryKey: ['products'] });
          queryClient.invalidateQueries({ queryKey: ['search-products'] });
          navigate('/');
        },
      });
    } else {
      const uploadInfo = await updateOtherProductImages(productData, formValues);
      await updateColorsWithUploadedImages(productData, formValues, uploadInfo);

      await uploadProductImageFetch({
        method: 'FILE_POST',
        url: URLS.uploadProductImage({
          productId: productData?.id,
        }),
        params: { image: formValues.image.file.originFileObj },
        showSuccessNotification: true,
        successMessage: 'Product created successfully.',
        onSuccess: () => {
          // Invalidate products cache to refetch lists with the new product
          queryClient.invalidateQueries({ queryKey: ['products'] });
          queryClient.invalidateQueries({ queryKey: ['search-products'] });
          navigate('/');
        },
      });
    }
  };

  const updateOtherProductImages = async (productData, formValues) => {
    const formData = new FormData();

    // Keep track of file order for mapping later
    const filesToUpload = [];

    // Add new images (files) to formData
    // Upload any file that has an originFileObj (meaning it's a new file to upload)
    formValues.uploadFileList.forEach((file, index) => {
      // Only upload files that have originFileObj (new files to upload)
      // Skip files that are already uploaded to the backend (have image ID and real URL)
      if (file.originFileObj) {
        formData.append('upload_images', file.originFileObj);
        filesToUpload.push(file);
      }
    });

    // Add deleted image IDs to formData
    formValues.deleteFileList.forEach(file => {
      formData.append('delete_images', file.uid);
    });

    // Only make the request if there are files to upload or delete
    const hasFilesToUpload = filesToUpload.length > 0;
    const hasFilesToDelete = formValues.deleteFileList.length > 0;

    if (hasFilesToUpload || hasFilesToDelete) {
      try {
        const response = await axios.post(
          `${baseURL}/${URLS.manageProductImages(productData?.id)}`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
              Authorization: `${authToken}`,
            },
          }
        );

        // Process uploaded images to ensure full URLs
        const uploadedImages = (response.data?.uploaded_images || []).map(img => ({
          ...img,
          // Ensure we have a full URL (prepend baseURL if it's a relative path)
          image_url: img.image_url?.startsWith('http') ? img.image_url : `${baseURL}${img.image_url}`,
          url: img.url?.startsWith('http') ? img.url : `${baseURL}${img.url || img.image_url}`
        }));

        // Return the uploaded images info for mapping
        return {
          uploadedFiles: filesToUpload,
          uploadedImages: uploadedImages
        };
      } catch (error) {
        console.error('Error uploading images:', error);
        console.error('Error response:', error.response?.data);
        return { uploadedFiles: [], uploadedImages: [] };
      }
    }

    return { uploadedFiles: [], uploadedImages: [] };
  };

  const createNewProduct = async formValues => {
    const translationsObject = languages.reduce((acc, language) => {
      acc[language.id] = {
        name_text: formValues[`name.${language.id}`],
        description_text: formValues[`description.${language.id}`],
      };
      return acc;
    }, {});

    let formattedColors =
      formValues.colors != null && Array.isArray(formValues.colors)
        ? formValues.colors.map(color => {
          // For staged images, we'll update the imageLink after upload
          // For now, keep the blob URL for reference
          const formattedColor = {
            ...color,
            hexcode:
              typeof color.hexcode === 'object' ? color.hexcode.toHexString() : color.hexcode,
          };
          // Remove linkedImageFile as it's only used for internal tracking and shouldn't be sent to backend
          // (File objects can't be JSON stringified)
          delete formattedColor.linkedImageFile;
          return formattedColor;
        })
        : null;

    await createProductFetch({
      url: URLS.createProduct({ categoryId: formValues.categoryId }),
      params: {
        ...translationsObject,
        price: formValues.price,
        dine_in_price: formValues.dineInPrice,
        price_wholesale: formValues.priceWholesale,
        currency: formValues.currency,
        discount: formValues.discount,
        calories: formValues.calories,
        subcategory_id: formValues.subcategoryId,
        brand_id: formValues.brandId,
        is_deliverable: formValues.isDeliverable,
        is_in_stock: formValues.isInStock,
        is_dinning: formValues.isDinning,
        is_recommended: formValues.isRecommended,
        is_new: formValues.isNew,
        is_special_offer: formValues.isSpecialOffer,
        is_trending: formValues.isTrending,
        is_best_seller: formValues.isBestSeller,
        types: JSON.stringify(formValues.types),
        colors: JSON.stringify(formattedColors),
        sizes: JSON.stringify(formValues.sizes),
        flavors: JSON.stringify(formValues.flavors || []),
        addon_ids: formValues.addon_ids || [],
      },
      onSuccess: data => uploadProductImage(data, formValues),
    });
  };

  // Merge preselected category with existing category list
  const finalCategoryList = React.useMemo(() => {
    let categories = [...(menuData.categoryList || [])];

    // Add preselected category if it exists and isn't already in the list
    if (preselectedCategory && !categories.find(cat => cat.id === preselectedCategory.id)) {
      categories = [preselectedCategory, ...categories];
    }

    return categories;
  }, [menuData.categoryList, preselectedCategory]);

  return (
    <Row>
      <CreateUpdateProductForm
        categoryList={finalCategoryList}
        subcategoryList={menuData.subcategoryList}
        brandList={menuData.brandList}
        languageList={languages.length > 0 ? languages : menuData.languageList}
        onSubmit={createNewProduct}
        // Pre-select category if provided in URL
        initialValues={preselectedCategoryId ? { categoryId: preselectedCategoryId } : undefined}
        onFormReady={setProductForm}
        // Search functionality
        searchCategories={searchCategories}
        searchSubcategories={searchSubcategories}
        searchBrands={searchBrands}
        isSearchingCategories={isSearchingCategories}
        isSearchingSubcategories={isSearchingSubcategories}
        isSearchingBrands={isSearchingBrands}
        onSearchCategories={handleSearchCategories}
        onSearchSubcategories={handleSearchSubcategories}
        onSearchBrands={handleSearchBrands}
        addonList={addonsData?.addons || []}
      />
    </Row>
  );
}

export default ProductCreate;
